import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, Mail, MapPin } from "lucide-react";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Your enquiry has been sent.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border border-white/15 text-white placeholder:text-zinc-600 px-4 py-3.5 font-mono text-sm focus:outline-none focus:border-cyan-400 transition-colors duration-300";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#070708] py-28 md:py-40 border-t border-white/10 grid-bg"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <FadeUp>
            <span className="overline">// Contact</span>
            <h2 className="mt-6 font-display font-700 text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white leading-[0.98]">
              Let&apos;s design your system.
            </h2>
            <p className="mt-8 text-lg leading-relaxed text-zinc-400 max-w-md">
              Have an embedded product, a firmware challenge or a full
              sensor-to-cloud platform in mind? Tell us what you&apos;re building.
            </p>

            <div className="mt-12 space-y-5">
              <div className="flex items-center gap-4 text-zinc-400">
                <Mail className="h-5 w-5 text-cyan-400" strokeWidth={1.5} />
                <span className="font-mono text-sm">hello@quantinium.co.uk</span>
              </div>
              <div className="flex items-center gap-4 text-zinc-400">
                <MapPin className="h-5 w-5 text-cyan-400" strokeWidth={1.5} />
                <span className="font-mono text-sm">United Kingdom · Remote & On-site</span>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-white/[0.02] border border-white/10 p-7 md:p-9 clip-frame"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                    Name
                  </label>
                  <input
                    data-testid="contact-name"
                    className={field}
                    placeholder="Your name"
                    value={form.name}
                    onChange={update("name")}
                  />
                </div>
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                    Email
                  </label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    className={field}
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                  Company <span className="text-zinc-700">(optional)</span>
                </label>
                <input
                  data-testid="contact-company"
                  className={field}
                  placeholder="Organisation"
                  value={form.company}
                  onChange={update("company")}
                />
              </div>

              <div className="mt-4">
                <label className="block font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                  Project brief
                </label>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  className={`${field} resize-none`}
                  placeholder="Tell us about the system you want to build..."
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="mt-6 group w-full inline-flex items-center justify-center gap-2 bg-cyan-400 text-black font-mono text-[13px] uppercase tracking-[0.14em] px-7 py-4 hover:bg-cyan-300 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed clip-frame-sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send enquiry
                    <ArrowUpRight className="h-4 w-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
                  </>
                )}
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default Contact;
